import { useMutation } from '@apollo/client';
import {
  AppContext,
  COMMON_CONSTANTS,
  EventEmitter,
  getCurrentUserHook,
  getTranslatedText,
  isAuthorised,
  NewSideConversation,
  SideConversations,
  TabsContainer,
  toast,
  useActivityTracker,
  useCheckFeatureGating,
  useUserMSPCheckHook,
  getConversationOrder,
  TICKET_SOURCE,
  ALL_MODULES
} from '@nile/components';
import {
  AccountClient,
  CREATE_TASKS,
  GET_TICKET_AUDIT_LIST,
  GET_TICKET_SUMMARY,
  GET_VENDOR_CONVERSATION_LIST,
  useCachedQuery,
  useQuery,
  GET_TASK_CONTENT_FROM_TEMPLATE,
  GET_IM_CONFIG_DETAILS,
  TicketClient
} from '@nile/graphql';
import { TicketTask } from '@nile/mangifera/src/Containers/ChatContainers';
import WorklogDetails from '@nile/mangifera/src/Containers/PageContainers/WorklogDetails';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import { SVGIcon } from '@superops/super-icons';
import React, { useContext, useEffect, useState } from 'react';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import ChatFeedContainerNew from '../../../Containers/ChatContainers/ChatFeedContainerNew';
import { Badge, TextStyle } from '@superops/ui-design-system';
import {
  EventEmitterWithIndex,
  getAIPrompts
} from '@nile/components/src/Utils/helpers';
import { isEmpty, pick } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTicketDetailsConversationStore } from '@nile/mangifera/src/StoreManager/store/ticketDetailsConversationStore';
import useSideConversationActions from '@nile/components/src/Components/Conversations/sideConversationActionsHook.tsx';

function TabContent(props) {
  return (
    <div className={`flex g8 center ${props?.hideCount ? 'mb-2' : ''}`}>
      <div className="tab-text flex center">
        <TextStyle>{props.content}</TextStyle>
      </div>
      {!props?.hideCount && (
        <div className="flex">
          <Badge>
            <TextStyle variation="subdued">{props.count || '0'}</TextStyle>
          </Badge>
        </div>
      )}
    </div>
  );
}

export function TicketConversation(props) {
  const {
    intl,
    ticket,
    showConfettiModal,
    updateTicketStatus,
    readOnly
  } = props;
  const flags = useFlags();
  const ticketConversationUx = flags?.ticketConversationUx;

  const context = useContext(TicketContext);
  const currentUser = getCurrentUserHook();
  const isMSP = useUserMSPCheckHook();
  const userRole = currentUser?.role?.roleFeatureList || [];
  let isEditAuthorised = isAuthorised(userRole, 'tickets.edit');
  const [updateTabCount, setUpdateTabCount] = useState(0);

  const CONVERSATION_MODE_MAP = {
    1: 'conversation',
    2: 'note'
  };

  if (typeof readOnly !== 'undefined') {
    isEditAuthorised = !readOnly;
  }

  // const [activeTabIndex, setActiveTabIndex] = useState(
  //   context?.activeTab?.state?.detailsTabIndex || 0
  // );
  const activeTabIndex = useTicketDetailsConversationStore(
    (s) => s.activeTabIndex
  );
  const setActiveTabIndex = useTicketDetailsConversationStore(
    (s) => s.setActiveTabIndex
  );

  const setConversationMode = useTicketDetailsConversationStore(
    (s) => s.setConversationMode
  );

  const setIsConversationSentFromTriageAgent = useTicketDetailsConversationStore(
    (s) => s.setIsConversationSentFromTriageAgent
  );

  const setTriageContent = useTicketDetailsConversationStore(
    (s) => s.setTriageContent
  );

  const newSideConversationLoading = useTicketDetailsConversationStore(
    (s) => s.newSideConversationLoading
  );

  const setNewSideConversationLoading = useTicketDetailsConversationStore(
    (s) => s.setNewSideConversationLoading
  );

  const [sideConversationToBeOpened, setSideConversationToBeOpened] = useState(
    null
  );

  const [sideConversationData, setSideConversationData] = useState([]);
  const [sideConversationLoading, setSideConversationLoading] = useState(false);

  // value from user preferred conversation order
  const conversationOrder = getConversationOrder() || 'ASC';

  const FeedsDataKey = {
    query: GET_TICKET_AUDIT_LIST,
    variables: {
      input: {
        ticket: { ticketId: ticket.ticketId },
        listInfo: {
          sort: {
            attribute: 'time',
            order: conversationOrder
          },
          condition: {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'type',
                operator: 'isNot',
                value: 'TASK'
              }
            ]
          }
        }
      }
    },
    key: 'getTicketAuditList',
    secondaryKey: 'ticketAudit'
  };

  const activeTabRef = React.useRef(0);
  activeTabRef.current = activeTabIndex;
  const [ticketSummary, setTicketSummary] = useState({});
  const [aiSideConversationModal, setAISideConversationModal] = useState(false);

  useEffect(() => {
    let vendorSideConversation =
      sideConversationData?.getVendorConversationsList?.conversations || [];
    if (vendorSideConversation?.length > 0) {
      if (conversationOrder === 'ASC') {
        //set conversationId in state to open latest conversation
        let openedConversationId =
          vendorSideConversation[vendorSideConversation.length - 1]
            ?.conversationId;
        openedConversationId &&
          setSideConversationToBeOpened(openedConversationId);
      }
    }
  }, [sideConversationData]);

  const [sideConversationTemplateId, setSideConversationTemplateId] = useState(
    null
  );

  const sideConversationWorkItem = {
    id: ticket?.ticketId,
    workId: ticket?.ticketId,
    module: ALL_MODULES.ticket,
    subject: ticket?.subject,
    displayId: ticket?.displayId
  };

  const {
    openChatPopup: openNewSideConversation,
    loading: newSideConversationPopupLoading
  } = useSideConversationActions({
    workItem: sideConversationWorkItem,
    module: ALL_MODULES.ticket
  });

  const openNewSideConversationPopup = (params) => {
    let paramsToPass = null;
    if (params?.templateId) {
      paramsToPass = { templateId: params?.templateId };
    }
    setNewSideConversationLoading(true);
    openNewSideConversation(paramsToPass)
      .then(() => {
        switchTabIndex(4);
        setNewSideConversationLoading(false);
      })
      .catch(() => {
        setNewSideConversationLoading(false);
      });
  };

  const openSideConversation = (params) => {
    if (ticketConversationUx) {
      openNewSideConversationPopup(params);
    } else {
      if (params?.templateId) {
        setSideConversationTemplateId(params?.templateId);
      }
      switchTabIndex(4);
      setAISideConversationModal(true);
    }
  };

  const insertTicketConversation = ({ content, timeout, attachments }) => {
    const outTime = timeout || 500;
    if (activeTabIndex !== 1) {
      switchTabIndex(1);
    }
    EventEmitter.dispatch('closeNewSideConversation');
    setTimeout(() => {
      EventEmitterWithIndex.dispatch('insertConversationWithAttachments', {
        content,
        attachments
      });
    }, outTime);
  };
  const insertNote = ({ content }) => {
    if (activeTabIndex !== 2) {
      switchTabIndex(2);
    }
    EventEmitter.dispatch('closeNewSideConversation');
    if (ticketConversationUx) {
      EventEmitterWithIndex.dispatch('insertTicketNote', { content });
    }
    // setTimeout(() => {
    //   EventEmitterWithIndex.dispatch('replaceEditorContent', content);
    // }, 500);
  };

  const AIPrompts = getAIPrompts();

  const triageContentInsertTimeOutRef = React.useRef(null);

  const insertTriageTicketConversation = (obj) => {
    const content = obj?.content;

    if (triageContentInsertTimeOutRef.current) {
      clearTimeout(triageContentInsertTimeOutRef.current);
    }

    if (ticketConversationUx) {
      if (activeTabRef.current !== 1) {
        switchTabIndex(1);
      }

      triageContentInsertTimeOutRef.current = setTimeout(() => {
        EventEmitterWithIndex.dispatch(
          'insertTriageTicketConversationToPopup',
          content
        );
      }, 500);
      return;
    }
    setIsConversationSentFromTriageAgent(true);

    if (activeTabRef.current !== 1) {
      // If the user is in another tab we'll switch to the conversation tab for the editor to open
      // Set the content to be inserted in the editor
      switchTabIndex(1);
      setTriageContent(content);
    } else {
      EventEmitterWithIndex.dispatch('insertEditorContent', content);
    }
  };

  useEffect(() => {
    EventEmitter.subscribe('worklogChange', updateTicketSummary);
    EventEmitter.subscribe(
      'openTicket.side.conversation',
      openSideConversation
    );
    const eventIndex = EventEmitterWithIndex.subscribe(
      'insertTicketConversation',
      insertTicketConversation
    );
    const triageInsertTicketIndex = EventEmitterWithIndex.subscribe(
      'insertTriageTicketConversation',
      insertTriageTicketConversation
    );
    const eventNoteIndex = EventEmitterWithIndex.subscribe(
      'insertNote',
      insertNote
    );
    return function cleanup() {
      console.log('unmounting');
      EventEmitter.unsubscribe('worklogChange');
      EventEmitter.unsubscribe('openTicket.side.conversation');
      EventEmitterWithIndex.unsubscribe('insertTicketConversation', eventIndex);
      EventEmitterWithIndex.unsubscribe(
        'insertTriageTicketConversation',
        triageInsertTicketIndex
      );
      EventEmitterWithIndex.unsubscribe('insertNote', eventNoteIndex);
      if (triageContentInsertTimeOutRef.current) {
        clearTimeout(triageContentInsertTimeOutRef.current);
      }
    };
  }, []);

  async function getSideConversationData() {
    setSideConversationLoading(true);
    const response = await AccountClient.query({
      query: GET_VENDOR_CONVERSATION_LIST,
      fetchPolicy: 'network-only',
      variables: {
        input: {
          module: 'TICKET',
          listInfo: {
            condition: {
              joinOperator: 'AND',
              operands: [
                {
                  joinOperator: 'AND',
                  operands: [
                    {
                      attribute: 'parentConversationId',
                      operator: 'isEmpty'
                    }
                  ]
                },
                {
                  joinOperator: 'AND',
                  operands: [
                    {
                      attribute: 'workItem.workId',
                      operator: 'is',
                      value: ticket.ticketId
                    }
                  ]
                }
              ]
            },
            sort: [
              {
                attribute: 'time',
                order: conversationOrder
              }
            ]
          }
        }
      }
    });

    if (response.data.getVendorConversationsList) {
      setSideConversationLoading(false);
      setSideConversationData(response.data);
    }
  }

  const refetchSideConversation = getSideConversationData;

  useEffect(() => {
    getSideConversationData();
  }, []);

  const toggleNewSideConversation = () => {
    if (ticketConversationUx) {
      openSideConversation();
    } else {
      setAISideConversationModal(!aiSideConversationModal);
    }
  };

  const switchToConnect = (data) => {
    const convoId = data?.parentConversationId || data?.conversationId;
    if (convoId && sideConversationToBeOpened !== convoId)
      setSideConversationToBeOpened(convoId);

    const ConnectTabIndex = authorisedTabs.findIndex((tab) => {
      return tab.id === 'connect';
    });
    if (ConnectTabIndex) {
      setActiveTabIndex(ConnectTabIndex);
    }
  };

  const {
    loading: ticketSummaryLoading,
    error: ticketSummaryError,
    data: ticketSummaryData,
    refetch
  } = useQuery(GET_TICKET_SUMMARY, {
    variables: { ticketId: ticket.ticketId },
    onCompleted({ getTicketSummary }) {
      setTicketSummary(getTicketSummary);
    }
  });

  const skipImConfig =
    ticketConversationUx ||
    !(
      flags?.imAppSupport === true &&
      ticket?.source === TICKET_SOURCE.Communication
    );

  const {
    data: imConfig,
    loading: imConfigLoading,
    error: imConfigError
  } = useQuery(GET_IM_CONFIG_DETAILS, {
    client: AccountClient,
    skip: skipImConfig
  });

  const [createTasks] = useMutation(CREATE_TASKS, {
    onCompleted() {
      toast('success', 'ticket.details.tasks.create.taskAssociate.success');
      useActivityTracker({
        event: 'Ticket - Associated Task to Ticket'
      });
    },
    onError() {
      // toast('error', 'Something went wrong');
    }
  });

  const updateVendorConversationCache = (data) => {
    const feedCache = AccountClient.readQuery({
      query: FeedsDataKey.query,
      variables: FeedsDataKey.variables
    });
    const updatedFeed = {
      approval: null,
      conversation: null,
      vendorConversation: data,
      note: null,
      task: null,
      type: 'conversation'
    };
    let feeds = [updatedFeed];

    if (FeedsDataKey.secondaryKey) {
      const prevItems =
        feedCache?.[FeedsDataKey.key]?.[FeedsDataKey.secondaryKey] || [];

      feeds = [...feeds, ...prevItems];
    } else {
      feeds = [...feedCache?.[FeedsDataKey.key], updatedFeed];
    }

    AccountClient.writeQuery({
      query: FeedsDataKey.query,
      variables: FeedsDataKey.variables,
      data: {
        [FeedsDataKey.key]: feeds
      }
    });
  };

  const onSideConversationSubmit = (data) => {
    refetchSideConversation();
    switchToConnect(data);
  };

  const createNewSideConversation = (emailSignature) => {
    function onSubmit(data) {
      refetchSideConversation();
      switchToConnect(data);
    }
    return (
      <NewSideConversation
        plain
        showOriginalMsg
        intl={intl}
        workItem={ticket}
        workItemId={ticket.ticketId}
        module="TICKET"
        updateCache={updateVendorConversationCache}
        onSubmit={onSubmit}
        backgroundThreadConversation={flags?.sideConversationBackgroundThread}
        templateId={sideConversationTemplateId}
        zIndex={100}
      />
    );
  };

  const handleCreateTasks = (taskList, callback) => {
    if (!taskList?.length) return;

    const taskTemplateIds = taskList
      .filter((task) => task.taskTemplateId)
      .map((task) => ({ taskTemplateId: task.taskTemplateId }));

    TicketClient.query({
      query: GET_TASK_CONTENT_FROM_TEMPLATE,
      variables: { input: taskTemplateIds },
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }).then((res) => {
      const templateTasks = res?.data?.getTaskContentFromTemplate || [];

      createTasks({
        variables: {
          taskInput: templateTasks.map((task) => ({
            ...pick(task, [
              'title',
              'description',
              'technician',
              'techGroup',
              'estimatedTime',
              'inlineImages',
              'scheduledStartDate',
              'dueDate',
              'status'
            ]),
            ticket: { ticketId: ticket.ticketId }
          }))
        }
      }).then(() => {
        if (callback) callback();
        props.refetchTicket();
      });
    });
  };

  /**
   * If we add a worklog when we are in worklog tab of details page, we need to
   * dynamically update the count. So we subscribe to the event and refetch the summary.
   */
  const updateTicketSummary = () => {
    refetch().then((res) => {
      setTicketSummary(res?.data?.getTicketSummary || {});
    });
  };

  const getTabContent = (t, suffix) => {
    switch (t) {
      case 'UPDATES':
        return (
          <TabContent
            content={getTranslatedText(intl, 'ticket.details.taskbar.upd')}
            count={updateTabCount}
            hideCount
          />
        );
      case 'CONVERSATION':
        return (
          <TabContent
            content={`${getTranslatedText(
              intl,
              'ticket.details.taskbar.convo'
            )}`}
            count={suffix}
          />
        );
      case 'NOTES':
        return (
          <TabContent
            content={`${getTranslatedText(
              intl,
              'ticket.details.taskbar.notes'
            )}`}
            count={suffix}
          />
        );
      case 'TASKS':
        return (
          <TabContent
            content={`${getTranslatedText(
              intl,
              'ticket.details.taskbar.tasks'
            )}`}
            count={suffix}
          />
        );
      case 'WORKLOG':
        return (
          <TabContent
            content={`${getTranslatedText(
              intl,
              'ticket.details.taskbar.worklog'
            )}`}
            count={suffix}
          />
        );
      case 'CONNECT':
        return (
          <TabContent
            content={`${getTranslatedText(
              intl,
              'app.common.sideConversation.title'
            )}`}
            // icon={<SVGIcon name="helpcenter" />}
            count={suffix}
          />
        );
      default:
        return t;
    }
  };

  const switchTabIndex = (idx) => {
    setActiveTabIndex(idx);
    const updatedConversationMode = CONVERSATION_MODE_MAP[idx];
    if (updatedConversationMode) {
      setConversationMode(updatedConversationMode);
    }
  };

  const isDescOrder = conversationOrder === 'DESC';

  const [notesLength, setNotesLength] = useState(0);
  const [conversationLength, setConversationLength] = useState(0);

  const isSideConversationAvailableInPlan = useCheckFeatureGating(
    'SIDE_CONVERSATIONS'
  );

  const updateNumber = (type, number) => {
    if (type === 'note') {
      setNotesLength(number);
    }

    if (type === 'conversation') {
      setConversationLength(number);
    }
  };

  const ticketTabs = [
    {
      id: 'all-updates',
      content: getTabContent('UPDATES'),
      iconName: 'Updates',
      accessibilityLabel: 'All updates',
      panelID: 'all-updates-content',
      component: (
        <>
          <ChatFeedContainerNew
            readOnly={readOnly}
            ticketId={ticket?.ticketId}
            client={ticket?.client}
            createdBy={ticket?.createdBy}
            requester={ticket?.requester}
            createdTime={ticket?.createdTime}
            switchToConnect={switchToConnect}
            createNewSideConversation={
              isSideConversationAvailableInPlan?.enabled && isEditAuthorised
            }
            sop={ticket?.sop}
            status={ticket?.status}
            source={ticket?.source}
            additionalRequester={ticket?.additionalRequester}
            updateTicketStatus={updateTicketStatus}
            updateNumber={updateNumber}
            sendClientEvent={props?.sendClientEvent}
            concurrentUsersTyping={
              props?.concurrentViewers?.length
                ? props?.concurrentViewers?.filter(
                    (viewer) => viewer?.state === 'ACTIVE' && viewer?.isTyping
                  )
                : []
            }
            setFailureConversationData={props?.setFailureConversationData}
            setClosureSaveLoading={props?.setClosureSaveLoading}
            setClosureModalActive={props?.setClosureModalActive}
            setErrorsOnClosure={props?.setErrorsOnClosure}
            updateTicketState={props?.updateTicketState}
            setUpdateTabCount={setUpdateTabCount}
            subject={ticket?.subject}
            displayId={ticket?.displayId}
            AIPrompts={AIPrompts}
            imConfig={imConfig?.getIMConfigDetails}
          />
        </>
      )
    },
    {
      id: 'accepts-conversions',
      content: getTabContent('CONVERSATION', conversationLength || 0),
      iconName: 'Conversation',
      panelID: 'accepts-conversion-content',
      component: (
        <>
          <ChatFeedContainerNew
            readOnly={readOnly}
            ticketId={ticket?.ticketId}
            client={ticket?.client}
            createdBy={ticket?.createdBy}
            requester={ticket?.requester}
            createdTime={ticket?.createdTime}
            switchToConnect={switchToConnect}
            sop={ticket?.sop}
            status={ticket?.status}
            source={ticket?.source}
            additionalRequester={ticket?.additionalRequester}
            updateTicketStatus={updateTicketStatus}
            updateNumber={updateNumber}
            filterBy={
              ticket?.source === TICKET_SOURCE.Communication
                ? ['CONVERSATION', 'THREAD_CONVERSATION']
                : ['CONVERSATION']
            }
            mode="conversation"
            refetchTicket={props.refetchTicket}
            showConfettiModal={showConfettiModal}
            isDescOrder={isDescOrder}
            sendClientEvent={props?.sendClientEvent}
            concurrentUsersTyping={
              props?.concurrentViewers?.length
                ? props?.concurrentViewers?.filter(
                    (viewer) => viewer?.state === 'ACTIVE' && viewer?.isTyping
                  )
                : []
            }
            setFailureConversationData={props?.setFailureConversationData}
            setClosureSaveLoading={props?.setClosureSaveLoading}
            setClosureModalActive={props?.setClosureModalActive}
            setErrorsOnClosure={props?.setErrorsOnClosure}
            updateTicketState={props?.updateTicketState}
            setUpdateTabCount={setUpdateTabCount}
            subject={ticket?.subject}
            displayId={ticket?.displayId}
            AIPrompts={AIPrompts}
            imConfig={imConfig?.getIMConfigDetails}
          />
        </>
      )
    },
    {
      id: 'accepts-notes',
      content: getTabContent('NOTES', notesLength || 0),
      iconName: 'Notes',
      panelID: 'accepts-marketing-content',
      authorisation: 'tickets.view',
      // component: <NoteContainer ticket={ticket}/>,
      component: (
        <>
          <ChatFeedContainerNew
            readOnly={readOnly}
            ticketId={ticket?.ticketId}
            client={ticket?.client}
            createdBy={ticket?.createdBy}
            requester={ticket?.requester}
            createdTime={ticket?.createdTime}
            switchToConnect={switchToConnect}
            sop={ticket?.sop}
            status={ticket?.status}
            additionalRequester={ticket?.additionalRequester}
            source={ticket?.source}
            updateTicketStatus={updateTicketStatus}
            updateNumber={updateNumber}
            filterBy={['NOTE']}
            mode="note"
            isDescOrder={isDescOrder}
            setUpdateTabCount={setUpdateTabCount}
            subject={ticket?.subject}
            displayId={ticket?.displayId}
            AIPrompts={AIPrompts}
          />
        </>
      )
    },
    {
      id: 'accepts-tasks',
      content: getTabContent('TASKS', ticket.totalTasks),
      iconName: 'Tasks',
      panelID: 'accepts-tasks-content',
      authorisation: 'tickets.edit',
      component: (
        <TicketTask
          createTasks={handleCreateTasks}
          ticket={ticket}
          refetchTicket={props.refetchTicket}
          readOnly={readOnly}
        />
      )
    },
    {
      id: 'worklog',
      content: getTabContent('WORKLOG', ticketSummary?.worklogCount || 0),
      iconName: 'Worklog',
      panelID: 'worklog',
      authorisation: 'tickets.edit',
      component: (
        <WorklogDetails
          isEditable={isAuthorised(userRole, 'tickets.edit')}
          ticket={ticket}
          id={ticket.ticketId}
          inputVariable="ticketId"
          refetchTicket={props.refetchTicket}
        />
      )
    },
    {
      id: 'connect',
      content: getTabContent(
        'CONNECT',
        sideConversationData?.getVendorConversationsList?.conversations
          .length || 0
      ),
      iconName: 'Helpcenter',
      panelID: 'connect',
      featureGating: isSideConversationAvailableInPlan?.enabled,
      component: (
        <SideConversations
          module="TICKET"
          workItem={ticket}
          workItemId={ticket.ticketId}
          refetch={refetchSideConversation}
          updateFeedsCache={updateVendorConversationCache}
          createNewSideConversation={isSideConversationAvailableInPlan?.enabled}
          vendorConversationLoading={sideConversationLoading}
          vendorConversations={
            sideConversationData?.getVendorConversationsList?.conversations ||
            []
          }
          conversationToBeOpened={sideConversationToBeOpened}
          showOriginalMsg
          readOnly={readOnly}
          modalOnly
          toggleNewSideConversation={toggleNewSideConversation}
          backgroundThreadConversation={flags?.sideConversationBackgroundThread}
          templateId={sideConversationTemplateId}
          newSideConversationLoading={newSideConversationLoading}
        />
      )
    }
  ].filter((tab) => {
    if (tab?.hasOwnProperty('featureGating')) {
      return tab?.featureGating;
    }
    return true;
  });

  let authorisedTabs = ticketTabs.filter((el) => {
    if (!el.authorisation) return true;
    const isFeatureAuthorised = isAuthorised(userRole, `${el.authorisation}`);
    return isFeatureAuthorised;
  });

  if (!isMSP) {
    authorisedTabs = authorisedTabs.filter((el) => el.id !== 'connect');
  }

  authorisedTabs = authorisedTabs.filter((el) => el.id !== 'worklog');

  return (
    <div className="full-height ticket-conversation-tabs">
      <TabsContainer
        adjustHeight
        onTabChange={switchTabIndex}
        activeTabIndex={activeTabIndex}
        tabs={authorisedTabs}
        noShadow
      />

      <NewSideConversation
        modalState={aiSideConversationModal}
        toggleState={(updatedState) => {
          setAISideConversationModal(updatedState);
        }}
        modalOnly
        showOriginalMsg
        intl={intl}
        workItem={ticket}
        workItemId={ticket.ticketId}
        module="TICKET"
        updateCache={updateVendorConversationCache}
        onSubmit={onSideConversationSubmit}
        AIPrompts={AIPrompts}
        backgroundThreadConversation={flags?.sideConversationBackgroundThread}
        templateId={sideConversationTemplateId}
        zIndex={100}
      />
    </div>
  );
}
